import React, { useRef, useEffect, ReactElement, useState } from "react";
import Head from "next/head";
import { Box, Flex, Text } from "rebass/styled-components";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";

import { stateToQueryParams, useSearchState } from "./SearchContext";
import { Dog, CountryServiceType } from "../../interfaces";
import SearchResultList from "./SearchResultList";
import { SearchQuery } from "../../generated/graphql";
import { resolveHref } from "../../lib/routing";
import Benefits from "./Benefits";
import { mainStyles } from "../../lib/styles/search";
import MobileFilters from "./filtersModal/SearchFiltersModal";
import DesktopFilters from "./boxFilters/BoxFilters";
import SortBy from "./SortBy";

const SearchMap = dynamic(() => import("./SearchMap"));

interface Props {
  countryServiceTypes: CountryServiceType[];
  userDogs: Dog[];
  searchResults: SearchQuery["search"];
  loading: boolean;
  error: any;
  fetchMore: any;
  h1Heading: string;
  subHeading?: string;
  h2Heading?: string;
  breadcrumb?: ReactElement;
  mobileMapVisible: boolean;
  setMobileMapVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isMobileViewport: boolean;
  query: any;
  isSEOLanding?: boolean;
}

function SearchPage(props: Props) {
  const {
    countryServiceTypes,
    searchResults,
    loading,
    error,
    fetchMore,
    h1Heading,
    subHeading,
    h2Heading,
    breadcrumb,
    mobileMapVisible,
    setMobileMapVisible,
    isMobileViewport,
    isSEOLanding = false
  } = props;

  const caregivers = searchResults ? searchResults.caregivers : [];
  const serviceType = searchResults ? searchResults.serviceType : null;

  const searchState = useSearchState();

  const [highlightedSitterIndex, setHighlightedSitterIndex] = useState(null);

  const isFirstRun = useRef(true);
  const currentPage = searchResults ? searchResults.currentPage : null;

  // When the searchState changes, (skipping the first render),
  // push a new route to persist that state into the URL query params
  const router = useRouter();
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    router.push(
      resolveHref(
        { pathname: "/search", search: stateToQueryParams(searchState) },
        router.locale!
      )!,
      undefined, // asPath deprecated in Next.js 10
      {
        shallow: true
      }
    );
  }, [searchState]);

  return (
    <>
      <Head key="searchRobots">
        {currentPage && currentPage > 1 && (
          <meta name="robots" content="noindex,follow" />
        )}
      </Head>

      {/* {!isMobileViewport && (
        <DesktopFilters countryServiceTypes={countryServiceTypes} />
      )} */}

      <Flex>
        <Box width={[1, 1, "500px"]}>
          {!mobileMapVisible && (
            <>
              <Box sx={mainStyles.header}>
                <Text as="h1" variant="text.h3" sx={mainStyles.title} mt={2}>
                  {h1Heading}
                </Text>

                {!!subHeading && (
                  <Text as="p" variant="text.h3" sx={mainStyles.subtitle}>
                    {subHeading}
                  </Text>
                )}
              </Box>

              <Box
                pr={0}
                sx={{
                  zIndex: 1
                }}
              >
                {/* {isMobileViewport && (
                  <Box px={2} pb={3}>
                    <MobileFilters
                      countryServiceTypes={countryServiceTypes}
                      onMap={() => setMobileMapVisible(true)}
                    />
                  </Box>
                )} */}

                <Box>
                  <Benefits isMobileViewport={isMobileViewport} />
                </Box>

                {/* {h2Heading && (
                  <Text as="h2" sx={mainStyles.heading}>
                    {h2Heading}
                  </Text>
                )} */}

                {/* <Box mx={2} mt={2} mb={1}>
                  {searchState.countryServiceType && <SortBy />}
                </Box> */}

                <Box pl={2}>
                  <SearchResultList
                    loading={loading}
                    error={error}
                    searchResults={searchResults}
                    onSitterMouseEnter={index =>
                      setHighlightedSitterIndex(index)
                    }
                    onSitterMouseLeave={() => setHighlightedSitterIndex(null)}
                    isSEOLanding={isSEOLanding}
                  />
                </Box>

                <Box px={4} my={3}>
                  {breadcrumb}
                </Box>
              </Box>
            </>
          )}

          {/* Mobile map */}
          {typeof window !== "undefined" &&
            isMobileViewport &&
            mobileMapVisible && (
              <SearchMap
                loading={loading}
                fetchMore={fetchMore}
                searchResults={caregivers}
                {...{
                  isMobileViewport,
                  mobileMapVisible,
                  setMobileMapVisible
                }}
                serviceType={serviceType}
              />
            )}
        </Box>

        {/* Desktop map */}
        <Box flex={1}>
          {typeof window !== "undefined" && !isMobileViewport && (
            <SearchMap
              loading={loading}
              searchResults={caregivers}
              fetchMore={fetchMore}
              serviceType={serviceType}
              highlightedSitterIndex={highlightedSitterIndex}
            />
          )}
        </Box>
      </Flex>
    </>
  );
}

export default SearchPage;
